import React, { useState } from 'react';
import { Hash, Loader2, AlertTriangle } from 'lucide-react';
import { fiatService } from '../../../../services/api';
import { toErrorMessage } from '../../../../utils/safeStr';
import { UTR, STYLES } from '../copy';
import { UTR_SAMPLE, UTR_SAMPLE_LINE_DATE } from '../examples';
import type { OffRampToolPanelProps } from '../types';
import { ResultEmptyHint } from '../shared/ResultEmptyHint';

export function UtrDecodePanel({ className, emphasizeExample, onComplete }: OffRampToolPanelProps) {
    const [utr, setUtr] = useState('');
    const [lineDate, setLineDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const runDecode = async (value: string, date: string) => {
        if (!value.trim()) {
            setError('Enter a UTR / RRN.');
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await fiatService.decodeUtr(value.trim(), date || undefined);
            setResult(res);
            onComplete?.();
        } catch (e) {
            setError(toErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const trySample = () => {
        setUtr(UTR_SAMPLE);
        setLineDate(UTR_SAMPLE_LINE_DATE);
        void runDecode(UTR_SAMPLE, UTR_SAMPLE_LINE_DATE);
    };

    return (
        <div className={`${STYLES.card} ${className ?? ''}`}>
            <ResultEmptyHint>{UTR.empty}</ResultEmptyHint>
            <div className="flex flex-col sm:flex-row gap-3">
                <input
                    className={STYLES.input}
                    placeholder="UTR / RRN, e.g. 412345678901 or HDFCR2026..."
                    value={utr}
                    onChange={(e) => setUtr(e.target.value)}
                    aria-label="UTR or RRN"
                />
                <input
                    className={`${STYLES.input} sm:max-w-[160px]`}
                    type="date"
                    value={lineDate}
                    onChange={(e) => setLineDate(e.target.value)}
                    title="Statement line date — tamper check"
                    aria-label="Statement line date"
                />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btn : STYLES.btnSecondary}
                    onClick={trySample}
                    disabled={loading}
                >
                    {UTR.trySample}
                </button>
                <button
                    type="button"
                    className={STYLES.btn}
                    onClick={() => void runDecode(utr, lineDate)}
                    disabled={loading}
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />}
                    {UTR.run}
                </button>
                {error && <span className="text-xs text-red-500">{error}</span>}
            </div>
            {result && (
                <div className="border border-border rounded-md p-4 bg-secondary/30 text-xs space-y-1">
                    <div className="flex items-center gap-2">
                        {result.valid ? (
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                                Valid {result.rail}
                            </span>
                        ) : (
                            <span className="text-red-500 font-semibold">{UTR.invalid}</span>
                        )}
                        {result.is_structured && (
                            <span className="text-muted-foreground">(self-describing)</span>
                        )}
                    </div>
                    <div className="font-mono text-[11px]">canonical: {result.canonical || '—'}</div>
                    {result.remitting_bank && <div>Remitting bank: {result.remitting_bank}</div>}
                    {result.embedded_date && <div>Embedded date: {result.embedded_date}</div>}
                    {result.consistency === 'date_mismatch' && (
                        <div className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {UTR.tamper}
                        </div>
                    )}
                    {result.format_warning && (
                        <div className="text-muted-foreground">note: {result.format_warning}</div>
                    )}
                </div>
            )}
        </div>
    );
}
